import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAllRecords, createRecord, updateRecord, deleteRecord } from '../api/records';
import { useRecordFilters } from "../hooks/useRecordFilters";
import { useRecordPagination } from "../hooks/useRecordPagination";
import RecordListFilters from "../components/records/RecordListFilters";
import RecordCard from '../components/records/RecordCard';
import RecordSheet from '../components/records/RecordsSheet';
import { useTranslation } from 'react-i18next';

const Records = () => {
    const queryClient = useQueryClient();
     const { t } = useTranslation();

    // Filters   
    const { search, worker, dateFrom, dateTo, setFilters } = useRecordFilters(); 

    // Pagination
    const { cursor, isFirstPage, handleNext, handlePrev} = useRecordPagination({ search, worker, dateFrom, dateTo });

    // Sheet State
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetMode, setSheetMode] = useState('add');
    const [selectedRecord, setSelectedRecord] = useState(null);

    // Records Query
    const {data = {records: [], hasNext: false }, isLoading} = useQuery({
        queryKey: ['records', {cursor, search, worker, dateFrom, dateTo}],
        queryFn: () => fetchAllRecords({ cursor, search, worker, dateFrom, dateTo })
    });
    const {records = [], hasNext, nextCursor } = data;

    // Handlers
    const handleAddOpen = () => {
        setSelectedRecord(null);
        setSheetMode('add');
        setSheetOpen(true);
    };
    const handleEditOpen = (record) => {
        setSelectedRecord(record);
        setSheetMode('edit');
        setSheetOpen(true);
    };
    const handleSubmit = (payload) => {
        const fn = sheetMode === 'add'
            ? createRecord(payload)
            : updateRecord(selectedRecord.id, payload);
        fn.then(() => {
            setSheetOpen(false);
            queryClient.invalidateQueries({ queryKey: ['records'] });
        });
    };
    const handleDelete = (id) => {
        if (!window.confirm('Delete this record?')) return;
        deleteRecord(id).then(() => queryClient.invalidateQueries({ queryKey: ['records'] }));
    };
    
    return (
        <div className="flex flex-col w-full max-w-md mx-auto px-3 pt-4 pb-24">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-white text-3xl font-bold">{t('records.records_title')}</h1>
                <button 
                onClick={handleAddOpen}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-secondary-800 text-white text-2xl font-bold">+</button>
            </div>

            {/* Filter */}
            <RecordListFilters onChange={setFilters} />
            <p className="text-primary-10 text-xs font-mono mt-2 mb-3">
                {records.length === 1 ? t('records.records_label_one', { count: records.length }) : t('records.records_label_other', { count: records.length })}
            </p>
            {/* Records List */}
            {isLoading ? (
                <p className="text-primary-50 text-sm text-center mt-8">{t('general.loading')}</p>
            ) : records.length === 0 ? (
                    <p className="text-primary-50 text-sm text-center mt-8">{t('records.records_search_empty')}</p>
            ) : (
                <div className="flex flex-col gap-3">
                    {records.map((record) => (
                        <RecordCard 
                            key={record.id}
                            record={record}
                            onEdit={handleEditOpen}
                            onDelete={handleDelete} />
                    ))}
                </div>
            )}
                
            { /* Pagination */ }
            {(!isFirstPage || hasNext ) && (
                <div className='flex items-center justify-between mt-4'>
                    <button
                        onClick={() => handlePrev()}
                        disabled={isFirstPage}
                        className='px-3 py-2 text-xs font-mono border border-primary-50 text-primary-50 rounded-xl disabled:opacity-30'>
                            {t('records.prev')}
                    </button>
                    <button
                        onClick={() => handleNext(nextCursor)}
                        disabled={!hasNext}
                        className='px-3 py-2 text-xs font-mono border border-primary-50 text-primary-50 rounded-xl disabled:opacity-30'>
                            {t('records.next')}
                    </button>
                </div> 
            )}
            {/* Add or Edit sheet */}
            <RecordSheet
                isOpen={sheetOpen}
                mode={sheetMode}
                record={selectedRecord}
                onClose={() => setSheetOpen(false)}
                onSubmit={handleSubmit} />
    </div>
    );
};

export default Records;